import React, { CSSProperties } from 'react';
import debounce from 'lodash/debounce';
import { compose, withState, withProps, onlyUpdateForKeys } from 'recompose';
import styled, { StylesProps, cx } from '../../../../../../lib/styles';
import { Brand } from '../../../../../../lib/theme';
import InputLabel from '../../../../../../components/presentational/Input/InputLabel';
import Icon from '../../../../../../components/presentational/Icon';

import typeaheadStyles from './Typeahead_styles';
import { InputValidationStatus } from '../../../../../../components/presentational/Input/InputStatus';

type InputSize = 'm' | 'l';

export interface Suggestion {
  label: string;
  name?: string;
  value: string;
  suggestionStyles: CSSProperties;
  options: any;
}

export interface TypeaheadProps {
  brand?: Brand;
  className?: string;
  type?: string;
  size?: InputSize;
  required?: boolean;
  label?: string;
  labelClassName?: string;
  inputClassName?: string;
  value?: any;
  status?: InputValidationStatus;
  suggestions: Suggestion[];
  onSuggest: (search: string) => Promise<Suggestion[]>;
  loadSuggestions: (search: string) => any;
  onChange?: (label: any, value: any, validSuggestion: boolean, options: any) => any;
  onBlur?: () => any;
  onFocus?: () => any;
  inputRef?: any;
  setInputRef: (ref: any) => any;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  setExpanded: (expanded: boolean) => any;
  expanded?: boolean;
  favourites: Suggestion[];
}

const debouncedLoad = debounce(
  (value: string, loadSuggestions: (search: string) => any) =>
    loadSuggestions(value),
  1000
);

const Typeahead = ({
  type = 'text',
  value = '',
  required,
  label,
  className,
  labelClassName,
  inputClassName,
  loadSuggestions,
  suggestions,
  onBlur,
  onFocus,
  status,
  onChange,
  placeholder,
  autoFocus,
  disabled,
  setExpanded,
  expanded,
  styles,
  favourites = [],
}: TypeaheadProps & StylesProps<typeof typeaheadStyles>) => {
  const [isHovering, setIsHovering] = React.useState(false);

  // useEffect(() => {
  //   console.log(suggestions);
  // });

  return (
    <div className={cx(styles.container, className)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        setExpanded(false);
      }}
    >
      {label && (
        <InputLabel
          className={labelClassName}
          required={required}
          status={status}
        >
          {label}
          {required && (<span className={styles.required}>*</span>)}
        </InputLabel>
      )}

      <input
        type={type}
        value={value}
        aria-label={label}
        required={required}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        onChange={({ target: { value } }) => {
          if (onChange) onChange(value, null, false, {});
          if (!value) setExpanded(true);
          debouncedLoad(value, loadSuggestions);
          // console.log(`Typeahead onChange`, {label, value, expanded});
        }}
        onBlur={() => {
          onBlur;
          !isHovering && setExpanded(false);
          // console.log(`Typeahead onBlur`, {label, expanded});
        }}
        onFocus={() => {
          if (favourites.length > 0) {
            setExpanded(true);
          }
          onFocus;
          // console.log(`Typeahead onFocus`, {label, expanded});
        }}
        className={cx(styles.input, inputClassName)}
      />
      {favourites.length > 1 && (
        <Icon
          color={'dark_grey'}
          icon={'expand_more'}
          onClick={() => setExpanded(!expanded)}
        />
      )}

      {expanded && (
        <div className="typeahead-results">
          {suggestions.length < 1 && favourites.length > 0 && (
            <div className="favourites">
             <p className={`favouritesHeading ${styles.favouritesHeading}`}>Favourites</p>
               {favourites.map(({ label, value, options }) => (
                <a
                  key={value}
                  className={`favourite`}
                  tabIndex={0}
                  onClick={() => {
                    setExpanded(false);
                    if (onChange) onChange(label, value, true, options);
                  }}
                >
                  {label}
                </a>
              ))}
            </div>
          )}

          <div className={`suggestions ${styles.suggestions}`}>
            {suggestions.map(({ label, value, suggestionStyles, name, options }) => (
              <a
                key={value}
                className={`suggestion ${styles.suggestion}`}
                style={suggestionStyles}
                tabIndex={0}
                onClick={() => {
                  setExpanded(false);
                  if (onChange) onChange(name ?? label, value, true, options);
                }}
                dangerouslySetInnerHTML={{
                  __html: label
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default compose(
  onlyUpdateForKeys([
    'suggestions',
    'expanded',
    'value',
    'onChange',
    'onBlur',
    'status',
  ]),
  withState('suggestions', 'setSuggestions', []),
  withState('expanded', 'setExpanded', false),
  withState('inputRef', 'setInputRef', undefined),
  withProps(({ onSuggest, setSuggestions, setExpanded }) => ({
    loadSuggestions: (search: string) => {
      if (!search) return setSuggestions([]);

      onSuggest(search)
        .catch(_ => {
          return [];
        })
        .then(suggestions => {
          setSuggestions(suggestions);
          setExpanded(true);
        });
    },
  })),
  styled(typeaheadStyles)
)(Typeahead);
